/**
 * Audit Log — read-only append-only event log
 */
import React, { useState, useCallback } from "react";
import { auditLog as auditApi } from "../api.js";
import { useAsync, useDebounce } from "../hooks.js";
import type { AuditEventType } from "@bizzness/shared-types";

const ADMIN_EVENTS: AuditEventType[] = [
  "tenant_suspended","tenant_reactivated","tenant_archived","tenant_deleted",
  "tenant_impersonated","tenant_plan_changed","subscription_upgraded","subscription_downgraded",
  "trial_extended","quota_reset","entitlement_granted","entitlement_revoked",
  "site_published","site_unpublished","cache_invalidated","site_rebuilt",
  "ai_provider_switched","admin_login","admin_logout","admin_session_revoked",
];

function eventBadge(type: string | null | undefined) {
  if (!type) return <span className="ops-badge ops-badge-neutral" style={{ fontSize: "0.65rem" }}>—</span>;
  if (type.includes("suspend") || type.includes("cancel") || type.includes("delete") || type.includes("fail"))
    return <span className="ops-badge ops-badge-red" style={{ fontSize: "0.65rem" }}>{type}</span>;
  if (type.includes("create") || type.includes("publish") || type.includes("success") || type.includes("grant"))
    return <span className="ops-badge ops-badge-green" style={{ fontSize: "0.65rem" }}>{type}</span>;
  if (type.includes("update") || type.includes("change") || type.includes("switch") || type.includes("extend"))
    return <span className="ops-badge ops-badge-blue" style={{ fontSize: "0.65rem" }}>{type}</span>;
  return <span className="ops-badge ops-badge-neutral" style={{ fontSize: "0.65rem" }}>{type}</span>;
}

export function AuditLogView() {
  const [tenantId, setTenantId]   = useState("");
  const [eventType, setEventType] = useState("");
  const [actorEmail, setActorEmail] = useState("");
  const [page, setPage]           = useState(1);
  const [expanded, setExpanded]   = useState<string | null>(null);

  const dTenant = useDebounce(tenantId, 350);
  const dActor  = useDebounce(actorEmail, 350);

  const fetcher = useCallback(
    () => auditApi.list({
      ...(dTenant    ? { tenantId: dTenant }       : {}),
      ...(eventType  ? { eventType }               : {}),
      ...(dActor     ? { actorEmail: dActor }      : {}),
      page: String(page), pageSize: "100",
    }),
    [dTenant, eventType, dActor, page]
  );
  const { data, loading, error, reload } = useAsync<any>(fetcher, [dTenant, eventType, dActor, page]);

  const rows: any[] = data?.items ?? [];

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <div>
          <h1 className="ops-page-title">Audit Log</h1>
          <p className="ops-page-subtitle">Read-only · append-only · never edited</p>
        </div>
        <button className="ops-btn ops-btn-secondary ops-btn-sm" onClick={reload}>↻ Refresh</button>
      </div>

      {error && <div className="ops-error">{error}</div>}

      <div className="ops-filters">
        <input className="ops-input" placeholder="Filter by tenant ID…"
          value={tenantId} onChange={e => { setTenantId(e.target.value); setPage(1); }} />
        <input className="ops-input" placeholder="Filter by actor email…"
          value={actorEmail} onChange={e => { setActorEmail(e.target.value); setPage(1); }} />
        <select className="ops-select" value={eventType} onChange={e => { setEventType(e.target.value); setPage(1); }}>
          <option value="">All event types</option>
          {ADMIN_EVENTS.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
      </div>

      {loading && <div className="ops-loading">Loading audit log…</div>}
      {!loading && rows.length === 0 && <div className="ops-empty">No entries match your filters.</div>}

      {rows.length > 0 && (
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Time</th><th>Event</th><th>Actor</th>
                <th>Tenant</th><th>Action</th><th>IP</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td style={{ fontSize: "0.7rem", color: "var(--ops-muted)", whiteSpace: "nowrap" }}>
                      {r.createdAt ? r.createdAt.slice(0, 19).replace("T", " ") : "—"}
                    </td>
                    <td>{eventBadge(r.eventType)}</td>
                    <td style={{ fontSize: "0.75rem" }}>
                      <div>{r.actorEmail ?? r.actorType ?? "—"}</div>
                      <div style={{ color: "var(--ops-muted)", fontSize: "0.7rem" }}>{r.actorType ?? ""}</div>
                    </td>
                    <td style={{ fontSize: "0.75rem", color: "var(--ops-muted)" }}>
                      {r.tenantId ? <code>{String(r.tenantId).slice(0, 8)}…</code> : "—"}
                    </td>
                    <td style={{ fontSize: "0.8125rem" }}>{r.action ?? "—"}</td>
                    <td style={{ fontSize: "0.7rem", color: "var(--ops-muted)" }}>{r.ipAddress ?? "—"}</td>
                    <td>
                      {(r.before || r.after) && (
                        <button className="ops-btn ops-btn-sm ops-btn-secondary"
                          onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                          {expanded === r.id ? "▲" : "▼"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: "0.5rem 0.875rem 0.75rem", background: "var(--ops-surface2)" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", fontSize: "0.75rem" }}>
                          {r.before && (
                            <div>
                              <div style={{ color: "var(--ops-muted)", marginBottom: "0.25rem" }}>Before</div>
                              <pre style={{ margin: 0, color: "var(--ops-text)", fontSize: "0.7rem", whiteSpace: "pre-wrap" }}>
                                {JSON.stringify(r.before, null, 2)}
                              </pre>
                            </div>
                          )}
                          {r.after && (
                            <div>
                              <div style={{ color: "var(--ops-muted)", marginBottom: "0.25rem" }}>After</div>
                              <pre style={{ margin: 0, color: "var(--ops-text)", fontSize: "0.7rem", whiteSpace: "pre-wrap" }}>
                                {JSON.stringify(r.after, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.hasMore && (
        <div className="ops-pagination">
          {page > 1 && <button className="ops-btn ops-btn-sm ops-btn-secondary" onClick={() => setPage(p => p - 1)}>← Prev</button>}
          <span>Page {page}</span>
          <button className="ops-btn ops-btn-sm ops-btn-secondary" onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
